import PropTypes from 'prop-types';

import Container, { ContainerProps as MuiContainerProps } from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { useGetPageContentQuery } from '../../services/seoContent';
import { CircularProgress } from '@classity-edtech/ui-base';
import { useConfig } from '../../contexts';
import Helmet from '../../components/Helmet';
import Header from '../../components/SEO/Header';
import { Footer } from '../../components/SEO/Footer';
import { Hero } from '../../components/SEO/Hero';
import { Pitch } from '../../components/SEO/Pitch';
import { Programs } from '../../components/SEO/Programs';
import { Features } from '../../components/SEO/Features';
import { FAQs } from '../../components/SEO/FAQs';
import { JoinUs } from '../../components/SEO/JoinUs';
import { extractFooterContent, extractHeaderContent } from '../../utils/extractContents';
import { useRef, useState, useEffect } from 'react';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import earningsPricing from '../../components/EarningsCalculator/pricing.json';
import { alignContent } from '@mui/system';

export interface TutorProps extends MuiContainerProps {}

export const Tutor = ({ className }: TutorProps) => {
  const screens = useAppMediaQuery();
  const smallScreens = screens.isMobile || screens.isTablet;

  const featuresRef = useRef(null);
  const programsRef = useRef(null);
  const faqsRef = useRef(null);
  const joinUsRef = useRef(null);

  const [programsVisible, setProgramsVisible] = useState(false);
  const [featuresVisible, setFeaturesVisible] = useState(false);
  const [faqsVisible, setFaqsVisible] = useState(false);
  const [joinUsVisible, setJoinUsVisible] = useState(false);

  const { learnUi } = useConfig() as any;
  const { data, isLoading: fetchingContent } = useGetPageContentQuery({
    tag: 'homePage',
    contentType: 'page',
  });

  const extractContent = () => {
    if (!!data) {
      return {
        seo: {
          title: data?.seo?.fields?.titleTag,
          keywords: data?.seo?.fields?.keywords,
          description: data?.seo?.fields?.metaDescription,
          metaRobotsIndex: data?.seo?.fields?.metaRobotsIndex,
        },
      };
    }
  };

  const extractHeroContent = () => {
    const content = data?.sections?.[0];

    return {
      title: content?.fields?.title ?? '',
      description: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
      primaryCta: content?.fields?.list?.[0]?.fields?.reference?.fields?.label ?? '',
      heroImage: content?.fields?.list?.[1]?.fields?.media?.fields?.image?.fields?.file?.url ?? '',
    };
  };

  const extractTeachingSection = () => {
    const content = data?.sections?.[1];

    return {
      title: content?.fields?.title ?? '',
      description: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
      items:
        content?.fields?.list?.map((item: any) => ({
          title: item?.fields?.title ?? '',
          description: item?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
          imageUrl: item?.fields?.media?.fields?.icon?.fields?.file?.url ?? '',
        })) ?? [],
    };
  };

  const extractTeachingOpportunities = () => {
    const content = data?.sections?.[2];

    if (!content?.fields?.list) return [];

    const items = content.fields.list.map(item => {
      const fields = item?.fields;

      const title = fields.title ?? '';

      const descriptions =
        fields.description?.content
          ?.map(block => {
            const values = block.content?.map(node => node.value)?.filter(Boolean) ?? [];

            if (values?.length === 1) {
              return values?.join('');
            }

            return null;
          })
          ?.filter(Boolean) ?? [];

      const tab = fields.reference?.fields?.label ?? '';

      const cta = fields.reference?.fields?.url ?? '';

      const imageUrl = fields.media?.fields?.image?.fields?.file?.url ?? null;

      const list =
        fields.description?.content
          ?.map(block => {
            const values = block.content?.map(node => node.value)?.filter(Boolean) ?? [];

            if (values.length >= 2) {
              return {
                title: values[0].trim(),
                description: values.slice(1).join('').trim(),
              };
            }

            return null;
          })
          ?.filter(Boolean) ?? [];

      return {
        title,
        descriptions,
        tab,
        cta,
        imageUrl,
        list,
      };
    });

    return {
      items,
      title: content?.fields?.title ?? '',
    };
  };

  const extractClassityHighlights = () => {
    const content = data?.sections?.[3];

    return {
      title: content?.fields?.title ?? '',
      description: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
      items:
        content?.fields?.list?.map((item: any) => ({
          title: item?.fields?.title ?? '',
          descriptions:
            item?.fields?.description?.content?.map(
              (block: any) => block?.content?.[0]?.value ?? '',
            ) ?? [],
          cta: item?.fields?.reference?.fields?.label ?? '',
          imageUrl: item?.fields?.media?.fields?.image?.fields?.file?.url ?? '',
        })) ?? [],
    };
  };

  const extractFaqSection = () => {
    const content = data?.sections?.[4];

    return {
      title: content?.fields?.title ?? '',
      faqs:
        content?.fields?.list?.map((item: any) => ({
          question: item?.fields?.title ?? '',
          answers:
            item?.fields?.description?.content?.map(
              (block: any) => block?.content?.[0]?.value ?? '',
            ) ?? [],
        })) ?? [],
    };
  };

  const extractTutorCtaSection = () => {
    const content = data?.sections?.[5];

    return {
      title: content?.fields?.title ?? '',
      description: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
      ctaLabel: content?.fields?.list?.[0]?.fields?.reference?.fields?.label ?? '',
    };
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.target === featuresRef.current) setFeaturesVisible(true);
            if (entry.target === programsRef.current) setProgramsVisible(true);
            if (entry.target === faqsRef.current) setFaqsVisible(true);
            if (entry.target === joinUsRef.current) setJoinUsVisible(true);
          }
        });
      },
      { threshold: 0.1 },
    );

    if (featuresRef.current) observer.observe(featuresRef.current);
    if (programsRef.current) observer.observe(programsRef.current);
    if (faqsRef.current) observer.observe(faqsRef.current);
    if (joinUsRef.current) observer.observe(joinUsRef.current);

    return () => observer.disconnect();
  }, [fetchingContent]);

  return (
    <>
      <Helmet
        title={extractContent()?.seo?.title}
        keywords={extractContent()?.seo?.keywords}
        description={extractContent()?.seo?.description}
        metaRobotsIndex={extractContent()?.seo?.metaRobotsIndex}
      />

      {fetchingContent ? (
        <Box
          sx={{
            width: '100%',
            height: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Header isExpanded {...extractHeaderContent(data)} hideNavigation={true} />

          <Hero
            isSmallerHeader
            showEarningsCalculator
            earningsPricing={earningsPricing}
            {...extractHeroContent()}
            sx={{ height: '80dvh', alignContent: 'center' }}
          />

          <Box sx={{ height: { xs: '72px', md: '144px' } }} />

          <Pitch
            background={globalColours['$global-colour-palette-gray-50']}
            sx={{
              borderTop: `1px solid ${globalColours['$global-colour-border-neutral']}`,
              borderBottom: `1px solid ${globalColours['$global-colour-border-neutral']}`,
            }}
            isSmallerHeader
            {...extractTeachingSection()}
          />

          <Box sx={{ height: '136px' }} />

          <Box id='main-content' sx={{ height: '8px' }}></Box>

          {/* <Box
            ref={programsRef}
            sx={{
              opacity: programsVisible ? 1 : 0,
              transform: programsVisible ? 'translateY(0)' : 'translateY(50px)',
              transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
            }}
          >
            <Programs {...extractTeachingOpportunities()} />
          </Box>

          <Box sx={{ height: '144px' }} /> */}

          <Box
            ref={featuresRef}
            sx={{
              opacity: featuresVisible ? 1 : 0,
              transform: featuresVisible ? 'translateY(0)' : 'translateY(50px)',
              transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
            }}
          >
            <Features contents={extractClassityHighlights()} />
          </Box>

          <Box sx={{ height: '144px' }} />

          <Box
            ref={faqsRef}
            sx={{
              opacity: faqsVisible ? 1 : 0,
              transform: faqsVisible ? 'translateY(0)' : 'translateY(50px)',
              transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
            }}
          >
            <FAQs {...extractFaqSection()} />
          </Box>

          <Box sx={{ height: '144px' }} />

          <Box
            ref={joinUsRef}
            sx={{
              opacity: joinUsVisible ? 1 : 0,
              transform: joinUsVisible ? 'translateY(0)' : 'translateY(50px)',
              transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
            }}
          >
            <JoinUs {...extractTutorCtaSection()} />
          </Box>

          <Footer {...extractFooterContent(data)} />
        </>
      )}
    </>
  );
};

Tutor.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Tutor.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Tutor)(
  ({ theme }) => `
`,
);
